import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { DocumentState } from "@/services/backend/documents/types/state";
import t from "@/lang/lang";
import {
  Milestone,
  MilestoneIcon,
  Milestones,
  MilestoneStatus,
  MilestoneTitle,
} from "@/shared/bsm/core/process/components/ui/process-page-overview";
import { useHtzOfferListQuery } from "@/services/backend/htz/offers/api/offer-api";
import {
  DocumentIconConfirmation,
  DocumentIconOffer,
  DocumentIconOrder,
  DocumentIconReport,
  DocumentIconWorkOrder,
} from "@/services/backend/documents/components/document-icon";
import { formatDate } from "@/shared/lib/utilities/date";
import { useHtzOrderListQuery } from "@/services/backend/htz/orders/api/order-api";
import { useHtzConfirmationListQuery } from "@/services/backend/htz/confirmations/api/confirmation-api";
import { useHtzWorkOrderListQuery } from "@/services/backend/htz/work-order/api/work-order-api";
import { useHtzReportListQuery } from "@/services/backend/htz/report/api/report-api";
import { useProcessesProcessShowQuery } from "@/services/backend/processes/api/process-api";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { RefreshCw } from "lucide-react";
import { GSMProcessKind } from "@/services/backend/processes/types/process-kind";

export function HtzMilestones({ processId }: { processId: string }) {
  const {
    data: process,
    isLoading,
    error,
  } = useProcessesProcessShowQuery({ id: processId });

  return (
    <Card className="pb-0">
      <CardHeader>
        <CardTitle>{t("Meilensteine")}</CardTitle>
        <CardDescription>
          {t(
            "Die Übersicht ist aktuell noch mehr illustrativ. Es wird lediglich ausgewertet ob ein entsprechendes Dokument erstellt wurde. Eine konkretere Logik wird folgen.",
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <RTKQueryErrorAlert error={error} />
        {isLoading && (
          <div className="flex items-center gap-2">
            <RefreshCw className="size-4 animate-spin" />
            <span>{t("Lade Meilensteine...")}</span>
          </div>
        )}
        {process && (
          <Milestones>
            {process.processKind === GSMProcessKind.Full && (
              <OfferMilestone processId={processId!} />
            )}
            {process.processKind === GSMProcessKind.Full && (
              <OrderMilestone processId={processId!} />
            )}
            {process.processKind === GSMProcessKind.Full && (
              <ConfirmationMilestone processId={processId!} />
            )}
            <WorkOrderMilestone processId={processId!} />
            <ReportMilestone processId={processId!} />
          </Milestones>
        )}
      </CardContent>
    </Card>
  );
}

function OfferMilestone({ processId }: { processId: string }) {
  const { data } = useHtzOfferListQuery({
    process: { active: true, values: [processId!] },
    state: { active: true, values: [DocumentState.Immutable] },
  });

  const offer = data?.items[data.items.length - 1];

  return (
    <Milestone>
      <MilestoneIcon
        icon={DocumentIconOffer}
        done={(data?.items.length ?? 0) > 0}
      />
      <MilestoneTitle>{t("Angebot versenden")}</MilestoneTitle>
      {offer && (
        <MilestoneStatus>
          {`Das letzte Angebot wurde am ${formatDate(offer.stateSetAt ?? "")} von ${offer.stateSetBy} versendet.`}
        </MilestoneStatus>
      )}
    </Milestone>
  );
}

function OrderMilestone({ processId }: { processId: string }) {
  const { data } = useHtzOrderListQuery({
    process: { active: true, values: [processId!] },
  });

  const order = data?.items[data.items.length - 1];

  return (
    <Milestone>
      <MilestoneIcon
        icon={DocumentIconOrder}
        done={(data?.items.length ?? 0) > 0}
      />
      <MilestoneTitle>{t("Auftragseingang")}</MilestoneTitle>
      {order && (
        <MilestoneStatus>
          {`Die letzte Bestellung ging am ${formatDate(order.date ?? "")} ein.`}
        </MilestoneStatus>
      )}
    </Milestone>
  );
}

function ConfirmationMilestone({ processId }: { processId: string }) {
  const { data } = useHtzConfirmationListQuery({
    process: { active: true, values: [processId!] },
    state: { active: true, values: [DocumentState.Immutable] },
  });

  const confirmation = data?.items[data.items.length - 1];

  return (
    <Milestone>
      <MilestoneIcon
        icon={DocumentIconConfirmation}
        done={(data?.items.length ?? 0) > 0}
      />
      <MilestoneTitle>{t("Auftragsbestätigung versendet")}</MilestoneTitle>
      {confirmation && (
        <MilestoneStatus>
          {`Die letzte Bestätigung wurde am ${formatDate(confirmation.date ?? "")} von ${confirmation.stateSetBy} versendet.`}
        </MilestoneStatus>
      )}
    </Milestone>
  );
}

function WorkOrderMilestone({ processId }: { processId: string }) {
  const { data } = useHtzWorkOrderListQuery({
    process: { active: true, values: [processId!] },
    state: { active: true, values: [DocumentState.Immutable] },
  });

  const workOrder = data?.items[data.items.length - 1];

  return (
    <Milestone>
      <MilestoneIcon
        icon={DocumentIconWorkOrder}
        done={(data?.items.length ?? 0) > 0}
      />
      <MilestoneTitle>{t("Arbeitsschein versendet")}</MilestoneTitle>
      {workOrder && (
        <MilestoneStatus>
          {`Die letzter Arbeitsschein wurde am ${formatDate(workOrder.endDate ?? "")} von ${workOrder.stateSetBy} versendet.`}
        </MilestoneStatus>
      )}
    </Milestone>
  );
}

function ReportMilestone({ processId }: { processId: string }) {
  const { data } = useHtzReportListQuery({
    process: { active: true, values: [processId!] },
    state: { active: true, values: [DocumentState.Immutable] },
  });

  const report = data?.items[data.items.length - 1];

  return (
    <Milestone>
      <MilestoneIcon
        icon={DocumentIconReport}
        done={(data?.items.length ?? 0) > 0}
      />
      <MilestoneTitle>{t("Bericht versendet")}</MilestoneTitle>
      {report && (
        <MilestoneStatus>
          {`Der letzte Bericht wurde am ${formatDate(report.date ?? "")} von ${report.stateSetBy} versendet.`}
        </MilestoneStatus>
      )}
    </Milestone>
  );
}
